import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogIn,
  Mail,
  Search,
  Trash2,
  UserCheck,
  UserPlus,
  UserX,
  X,
} from "lucide-react";
import {
  fetchPremiumSocial,
  fetchPrivateMessages,
  respondFriendRequest,
  sendFriendRequest,
  sendPrivateMessage,
} from "../../../api/premiumSocialService";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { useProfileHub } from "../ProfileHubContext";

function FriendActionButton({ children, onClick, tone = "dark", icon: Icon }) {
  const tones = {
    dark: "bg-slate-950 text-white",
    green: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-rose-600 text-white",
    light: "bg-white text-slate-700 border border-slate-200",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${tones[tone] || tones.dark}`}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      {children}
    </button>
  );
}

function SearchableUserPicker({ users, value, onChange, placeholder }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = users.find((u) => String(u.id) === String(value));
  const filtered = query.trim()
    ? users.filter((u) => u.username.toLowerCase().includes(query.toLowerCase()))
    : users;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const select = (user) => {
    onChange(String(user.id));
    setQuery(user.username);
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={selected && !open ? selected.username : query}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); onChange(""); }}
          placeholder={placeholder}
          className="h-11 w-full rounded-2xl border border-slate-200 bg-white pl-9 pr-8 text-sm text-slate-700 focus:border-sky-300 focus:outline-none"
        />
        {(value || query) && (
          <button type="button" onClick={clear} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-2xl border border-slate-200 bg-white shadow-lg">
          {filtered.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onMouseDown={() => select(u)}
                className="w-full px-4 py-2.5 text-left text-sm font-medium text-slate-700 hover:bg-sky-50 hover:text-sky-700"
              >
                {u.username}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && query && filtered.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-lg">
          Aucun résultat pour « {query} »
        </div>
      )}
    </div>
  );
}

export default function ProfileFriendsSection() {
  const navigate = useNavigate();
  const { profileIdentity } = useProfileHub();
  const [social, setSocial] = useState({
    friends: [],
    online_friends: [],
    suggested_users: [],
    game_invites: [],
    message_contacts: [],
    private_messages_blocked: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");
  const [thread, setThread] = useState([]);
  const [forms, setForms] = useState({
    friendId: "",
    privateBody: "",
  });

  const refreshSocial = async (signal) => {
    const payload = await fetchPremiumSocial(signal);
    setSocial((prev) => ({ ...prev, ...payload }));
    return payload;
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    refreshSocial(controller.signal)
      .catch(() => setError("Chargement social impossible."))
      .finally(() => setIsLoading(false));
    return () => controller.abort();
  }, []);

  const acceptedFriends = useMemo(
    () => (social.friends || []).filter((f) => f.status === "accepted"),
    [social.friends]
  );
  const pendingFriends = useMemo(
    () => (social.friends || []).filter((f) => f.status === "pending"),
    [social.friends]
  );
  const receivedRequests = useMemo(
    () => pendingFriends.filter((f) => f.direction === "received"),
    [pendingFriends]
  );
  const sentRequests = useMemo(
    () => pendingFriends.filter((f) => f.direction === "sent"),
    [pendingFriends]
  );
  const playingFriends = useMemo(
    () => (social.online_friends || []).filter((f) => f.online),
    [social.online_friends]
  );
  const selectedContact = (social.message_contacts || []).find(
    (c) => String(c.id) === String(selectedContactId)
  );

  const updateForm = (field, value) =>
    setForms((prev) => ({ ...prev, [field]: value }));

  const runAction = async (callback) => {
    setMessage("");
    setError("");
    try {
      const response = await callback();
      setMessage(response.message || "Action effectuee.");
      await refreshSocial();
      return response;
    } catch (actionError) {
      setError(
        actionError?.response?.data?.error ||
          actionError?.message ||
          "Action impossible."
      );
      return null;
    }
  };

  const loadThread = async (userId) => {
    setSelectedContactId(userId);
    setThread([]);
    if (!userId) return;
    try {
      const response = await fetchPrivateMessages(userId);
      setThread(response.messages || []);
    } catch {
      setError("Conversation indisponible.");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-[28px] bg-white/60" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {message && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {message}
        </div>
      )}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Amis en partie */}
      <ProfileSectionCard title="Amis en partie" subtitle="Présence live actuelle">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {playingFriends.map((item) => (
            <div
              key={item.user.id}
              className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-black text-slate-950">
                    {item.pseudo || item.user.username}
                  </div>
                  <div className="mt-1 text-xs font-semibold text-emerald-700">
                    En partie dans {item.category}
                  </div>
                </div>
                <span className="rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-white">
                  Online
                </span>
              </div>
              <div className="mt-3">
                <FriendActionButton
                  tone="green"
                  icon={LogIn}
                  onClick={() => navigate(`/game-quiz/${item.category}`)}
                >
                  Rejoindre
                </FriendActionButton>
              </div>
            </div>
          ))}
          {playingFriends.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500 md:col-span-2 xl:col-span-3">
              Aucun ami n'est en partie pour le moment.
            </div>
          )}
        </div>
      </ProfileSectionCard>

      {/* Mes amis + Ajouter */}
      <div className="grid gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <ProfileSectionCard title="Mes amis" subtitle="Relations acceptées et demandes">
          <div className="space-y-3">
            {acceptedFriends.map((item) => (
              <div
                key={item.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <Link
                    to={`/joueur/u/${item.friend.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-slate-950 hover:text-pixy-blue hover:underline"
                  >
                    {item.friend.username}
                  </Link>
                  <div className="text-xs text-slate-500">Ami accepté</div>
                </div>
                <FriendActionButton
                  tone="red"
                  icon={Trash2}
                  onClick={() => runAction(() => respondFriendRequest(item.id, "remove"))}
                >
                  Supprimer
                </FriendActionButton>
              </div>
            ))}
            {acceptedFriends.length === 0 && (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                Aucun ami accepté pour le moment.
              </div>
            )}

            {receivedRequests.length > 0 && (
              <div className="pt-2">
                <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                  Demandes reçues
                </div>
                <div className="space-y-2">
                  {receivedRequests.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <div className="text-sm font-semibold text-slate-800">
                        {item.friend.username}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <FriendActionButton
                          tone="green"
                          icon={UserCheck}
                          onClick={() => runAction(() => respondFriendRequest(item.id, "accept"))}
                        >
                          Accepter
                        </FriendActionButton>
                        <FriendActionButton
                          tone="light"
                          icon={UserX}
                          onClick={() => runAction(() => respondFriendRequest(item.id, "decline"))}
                        >
                          Refuser
                        </FriendActionButton>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sentRequests.length > 0 && (
              <div className="pt-2">
                <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                  Demandes envoyées
                </div>
                <div className="space-y-2">
                  {sentRequests.map((item) => (
                    <div
                      key={item.id}
                      className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <div className="text-sm font-semibold text-slate-800">
                          {item.friend.username}
                        </div>
                        <div className="text-xs text-slate-500">En attente</div>
                      </div>
                      <FriendActionButton
                        tone="light"
                        icon={X}
                        onClick={() => runAction(() => respondFriendRequest(item.id, "cancel"))}
                      >
                        Annuler
                      </FriendActionButton>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ProfileSectionCard>

        <ProfileSectionCard title="Ajouter un ami" subtitle="Envoyer une demande d'amitié">
          <div className="grid gap-3">
            <SearchableUserPicker
              users={social.suggested_users || []}
              value={forms.friendId}
              onChange={(v) => updateForm("friendId", v)}
              placeholder="Rechercher un joueur..."
            />
            <button
              type="button"
              onClick={() => runAction(() => sendFriendRequest(Number(forms.friendId)))}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-black text-white"
            >
              <UserPlus className="h-4 w-4" />
              Envoyer une demande
            </button>
          </div>
        </ProfileSectionCard>
      </div>

      {/* Messagerie */}
      <div className="grid gap-6">
        <ProfileSectionCard title="Messagerie privée" subtitle="Conversation entre amis">
          {social.private_messages_blocked ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm font-semibold text-amber-800">
              Messagerie indisponible pendant une partie.
            </div>
          ) : (
            <div className="grid gap-3">
              <SearchableUserPicker
                users={social.message_contacts || []}
                value={selectedContactId}
                onChange={(v) => loadThread(v)}
                placeholder="Rechercher un contact..."
              />
              <div className="min-h-40 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                {thread.map((item) => (
                  <div
                    key={item.id}
                    className={`mb-2 rounded-2xl px-3 py-2 text-sm ${
                      item.sender.id === profileIdentity.id
                        ? "bg-amber-100 text-amber-900"
                        : "bg-white text-slate-700"
                    }`}
                  >
                    <span className="font-bold">{item.sender.username}: </span>
                    {item.body}
                  </div>
                ))}
                {thread.length === 0 && (
                  <div className="text-sm text-slate-500">
                    {selectedContact ? "Aucun message." : "Sélectionne un ami."}
                  </div>
                )}
              </div>
              <textarea
                value={forms.privateBody}
                onChange={(e) => updateForm("privateBody", e.target.value)}
                rows={3}
                placeholder="Écrire un message..."
                className="rounded-2xl border border-slate-200 px-3 py-3 text-sm"
              />
              <button
                type="button"
                onClick={async () => {
                  const response = await runAction(() =>
                    sendPrivateMessage({
                      receiverId: Number(selectedContactId),
                      body: forms.privateBody,
                    })
                  );
                  if (response && selectedContactId) {
                    updateForm("privateBody", "");
                    await loadThread(selectedContactId);
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-black text-white"
              >
                <Mail className="h-4 w-4" />
                Envoyer
              </button>
            </div>
          )}
        </ProfileSectionCard>
      </div>
    </div>
  );
}
